// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { ANOVATable } from '@/components/results/SharedTables';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

export function AMMIView({ data, analysisId }) {
  const [deckLoading, setDeckLoading] = React.useState(false);
  if (!data) return null;

  const downloadStabilityDeck = async () => {
    if (!analysisId) { toast.error('Analysis id unavailable'); return; }
    setDeckLoading(true);
    try {
      const resp = await axios.get(
        `${API}/document/stability-deck/${analysisId}.pptx`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(
        new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `BKAgriD_StabilityDeck_${analysisId.slice(0, 8)}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Stability deck downloaded');
    } catch (e) {
      toast.error('Deck export failed');
    } finally {
      setDeckLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ammi-view">
      <div className="flex justify-end">
        <button
          onClick={downloadStabilityDeck}
          disabled={deckLoading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-[#16A085] text-white hover:bg-[#12876F] transition-colors disabled:opacity-60"
          data-testid="ammi-stability-deck-btn"
          title="Download a committee-ready AMMI/GGE stability slide deck (.pptx)"
        >
          <Download size={15} />
          {deckLoading ? 'Building deck…' : 'Stability Deck (PPTX)'}
        </button>
      </div>
      {data.interpretation && (
        <div className="bg-[#E8F6EF] rounded-2xl p-5 border border-[#16A085]/20">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Sub-Batch 3S-Tail — PC decomposition honesty note */}
      {data.pc_decomposition_note && (
        <div className="bg-[#F0FAF6] border-l-4 border-[#16A085] px-4 py-3 rounded-md" data-testid="ammi-pc-note">
          <p className="text-[12px] text-[#2C2A28] italic leading-relaxed">{data.pc_decomposition_note}</p>
        </div>
      )}

      {/* Sub-Batch 3S — IPCA-Partitioned ANOVA table (replaces basic ANOVA) */}
      <ANOVATable anovaTable={data.anova_table} title="AMMI ANOVA (IPCA-Partitioned)" color="#16A085" />

      {/* Sub-Batch 3S-Tail — Model selection verdict (Gauch 1988) */}
      {data.model_selection_verdict && (
        <div className="bg-[#FFF8E7] border-l-4 border-[#F39C12] px-4 py-3 rounded-md" data-testid="ammi-model-verdict">
          <p className="text-[12px] text-[#2C2A28] leading-relaxed">
            <strong className="text-[#B8770D]">Verdict — {data.selected_ammi_model}:</strong>{' '}{data.model_selection_verdict}
          </p>
        </div>
      )}

      {/* Legacy combined chart — fallback for historical analyses without the new biplots */}
      {data.chart && !data.ammi1_biplot && !data.ammi2_biplot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden">
          <div className="bg-[#16A085]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#16A085] uppercase tracking-wider">Combined IPCA + ASV Summary (legacy)</h4>
          </div>
          <img src={`data:image/png;base64,${data.chart}`} alt="AMMI Biplot" className="w-full" data-testid="ammi-chart" />
        </div>
      )}

      {/* Sub-Batch 3S — Separate AMMI1 + AMMI2 biplots */}
      {data.ammi1_biplot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="ammi1-biplot">
          <div className="bg-[#16A085]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#16A085] uppercase tracking-wider">AMMI1 Biplot — Mean × IPCA1</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Zobel-Wright-Gauch 1988 · Genotypes &amp; environments share the plot</p>
          </div>
          <img src={`data:image/png;base64,${data.ammi1_biplot}`} alt="AMMI1 Biplot" className="w-full" />
        </div>
      )}
      {data.ammi2_biplot && (
        <div className="rounded-2xl border border-[#E8E6E1] overflow-hidden" data-testid="ammi2-biplot">
          <div className="bg-[#16A085]/5 px-4 py-2 border-b border-[#E8E6E1]">
            <h4 className="font-semibold text-sm text-[#16A085] uppercase tracking-wider">AMMI2 Biplot — IPCA1 × IPCA2</h4>
            <p className="text-[11px] text-[#6E6B66] mt-0.5">Gauch &amp; Zobel 1996 · Genotypes near origin = most stable</p>
          </div>
          <img src={`data:image/png;base64,${data.ammi2_biplot}`} alt="AMMI2 Biplot" className="w-full" />
        </div>
      )}

      {/* Sub-Batch 3S — YSI (Yield Stability Index) table */}
      {data.ysi_table && data.ysi_table.length > 0 && (
        <div className="overflow-x-auto" data-testid="ammi-ysi">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Yield Stability Index (YSI = Yield Rank + ASV Rank · lower is better)</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#16A085] text-white">
              <th className="px-3 py-2 text-left">Genotype</th>
              <th className="px-3 py-2 text-center">Mean Yield</th>
              <th className="px-3 py-2 text-center">Yield Rank</th>
              <th className="px-3 py-2 text-center">ASV</th>
              <th className="px-3 py-2 text-center">ASV Rank</th>
              <th className="px-3 py-2 text-center">YSI</th>
              <th className="px-3 py-2 text-center">YSI Rank</th>
            </tr></thead>
            <tbody>
              {data.ysi_table.map((r, i) => (
                <tr key={r.Genotype} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F9F5]'}>
                  <td className="px-3 py-1.5 font-medium">{r.Genotype}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.Mean_Yield}</td>
                  <td className="px-3 py-1.5 text-center">{r.Yield_Rank}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{r.ASV}</td>
                  <td className="px-3 py-1.5 text-center">{r.ASV_Rank}</td>
                  <td className="px-3 py-1.5 text-center font-mono font-bold">{r.YSI}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${r.YSI_Rank === 1 ? 'bg-green-100 text-green-700' : r.YSI_Rank >= data.ysi_table.length - 1 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                      {r.YSI_Rank}{r.YSI_Rank === 1 ? ' (Best)' : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Genotype Means */}
        {data.genotype_means && (
          <div className="overflow-x-auto">
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Genotype Means</h4>
            <table className="w-full text-xs border-collapse" data-testid="ammi-geno-means">
              <thead><tr className="bg-[#16A085] text-white">
                <th className="px-3 py-2 text-left">Genotype</th><th className="px-3 py-2 text-center">Mean Yield</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.genotype_means).map(([g, v], i) => (
                  <tr key={g} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F9F5]'}>
                    <td className="px-3 py-1.5 font-medium">{g}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{typeof v === 'number' ? v.toFixed(4) : v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ASV Stability */}
        {data.asv && (
          <div className="overflow-x-auto">
            <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">AMMI Stability Value (ASV)</h4>
            <table className="w-full text-xs border-collapse" data-testid="ammi-asv">
              <thead><tr className="bg-[#16A085] text-white">
                <th className="px-3 py-2 text-left">Genotype</th><th className="px-3 py-2 text-center">ASV</th><th className="px-3 py-2 text-center">Rank</th>
              </tr></thead>
              <tbody>
                {Object.entries(data.asv).sort((a, b) => a[1] - b[1]).map(([g, v], i) => (
                  <tr key={g} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F9F5]'}>
                    <td className="px-3 py-1.5 font-medium">{g}</td>
                    <td className="px-3 py-1.5 text-center font-mono">{v.toFixed(4)}</td>
                    <td className="px-3 py-1.5 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${i === 0 ? 'bg-green-100 text-green-700' : i >= Object.keys(data.asv).length - 2 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {i + 1}{i === 0 ? ' (Most Stable)' : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* IPCA Scores */}
      {data.ipca1_genotypes && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Genotype IPCA Scores</h4>
          <table className="w-full text-xs border-collapse" data-testid="ammi-ipca">
            <thead><tr className="bg-[#16A085] text-white">
              <th className="px-3 py-2 text-left">Genotype</th><th className="px-3 py-2 text-center">IPCA 1</th><th className="px-3 py-2 text-center">IPCA 2</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.ipca1_genotypes).map(([g, v1], i) => (
                <tr key={g} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F5F9F5]'}>
                  <td className="px-3 py-1.5 font-medium">{g}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v1.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{data.ipca2_genotypes?.[g]?.toFixed(4) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sub-Batch 3S — Environment IPCA Scores */}
      {data.ipca1_environments && (
        <div className="overflow-x-auto" data-testid="ammi-env-ipca">
          <h4 className="font-semibold text-[#2C2A28] mb-2 text-sm uppercase tracking-wider">Environment IPCA Scores</h4>
          <table className="w-full text-xs border-collapse">
            <thead><tr className="bg-[#E74C3C] text-white">
              <th className="px-3 py-2 text-left">Environment</th>
              <th className="px-3 py-2 text-center">Mean Yield</th>
              <th className="px-3 py-2 text-center">IPCA 1</th>
              <th className="px-3 py-2 text-center">IPCA 2</th>
            </tr></thead>
            <tbody>
              {Object.entries(data.ipca1_environments).map(([e, v1], i) => (
                <tr key={e} className={i % 2 === 0 ? 'bg-white' : 'bg-[#FBEDEB]'}>
                  <td className="px-3 py-1.5 font-medium">{e}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{data.environment_means?.[e]?.toFixed(4) || '-'}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{v1.toFixed(4)}</td>
                  <td className="px-3 py-1.5 text-center font-mono">{data.ipca2_environments?.[e]?.toFixed(4) || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PC Variance */}
      {data.pc_variance_explained && (
        <div className="flex gap-3 flex-wrap">
          {data.pc_variance_explained.map((v, i) => (
            <div key={i} className="bg-[#16A085]/10 rounded-xl px-4 py-3 text-center border border-[#16A085]/20">
              <div className="text-lg font-bold text-[#16A085]">{v}%</div>
              <div className="text-[10px] text-[#8A8580] uppercase">PC{i + 1} Variance</div>
            </div>
          ))}
        </div>
      )}

      {data.classroom_mode && (
        <div className="bg-[#FFF8E1] rounded-2xl p-5 border border-amber-200 mt-4">
          <h4 className="font-bold text-amber-800 text-sm mb-2 uppercase tracking-wider">PhD Classroom Mode</h4>
          <pre className="text-xs text-amber-900 whitespace-pre-wrap leading-relaxed font-mono">{data.classroom_mode}</pre>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   EBERHART-RUSSELL STABILITY VIEW (Sub-Batch 3S)
============================================================ */
